// featureStore.ts
import { Module } from 'vuex'
import { table } from '../enum/PhoneIntegration'
import { cloneDeep, isEmpty } from 'lodash'
import {
  SubAccount,
  Notification,
  Migration,
  MigrationType,
  StoreFetchTrace,
  TwilioModalConfiguration,
  TwilioLoginModalType,
  TwilioForm,
  AgencyTwilio,
  PhoneNumber,
} from '../types/TwilioAccount'
import {
  TwilioAccountService,
  CompanyService,
  UserService,
  MoveNumbersService,
} from '../services'
import { RootState } from '../../../store/state_models'

// Store state interface
export interface PhoneIntegrationStoreModule {
  loading: boolean
  agencyTwilioLinked: boolean
  agencyLCPhoneLinked: boolean
  totalSubAccounts: number
  tablePage: number
  searchQuery: string
  subAccounts: SubAccount[]
  subAccount: SubAccount
  phoneNumbers: Record<string, PhoneNumber>
  migration: Migration
  notification: Notification
  traceFetchReq: StoreFetchTrace
  agencyTwilio: AgencyTwilio
  twilioModalForm: TwilioForm
  twilioModal: TwilioModalConfiguration
  stripe: any
}

export const phoneIntegration: Module<
  PhoneIntegrationStoreModule,
  RootState
> = {
  namespaced: true,
  state: {
    loading: false,
    agencyTwilioLinked: false,
    agencyLCPhoneLinked: false,
    totalSubAccounts: 0,
    tablePage: 1,
    searchQuery: '',
    subAccounts: [],
    subAccount: {},
    phoneNumbers: {},
    migration: {
      type: '',
      modal: false,
    },
    notification: {
      visible: false,
      title: '',
      description: '',
      content: '',
      meta: '',
      duration: 0,
      action: '',
    },
    traceFetchReq: {
      subAccounts: '',
    },
    twilioModalForm: {
      accountSid: '',
      authToken: '',
      autoSubAccounts: false,
    },
    twilioModal: {
      context: '' as TwilioLoginModalType,
      showModal: false,
      cancelCallback: undefined,
      successCallback: undefined,
      failureCallback: undefined,
    },
    agencyTwilio: {
      id: '',
      accountSid: '',
      companyId: '',
      traceId: '',
    },
    stripe: {},
  } as PhoneIntegrationStoreModule,

  getters: {
    agencyLinkageStatus: state => ({
      twilio: state.agencyTwilioLinked,
      LCPhone: state.agencyLCPhoneLinked,
    }),

    notificationDetail: state => state.notification,
  },

  mutations: {
    linkAgencyTwilio(state) {
      state.agencyTwilioLinked = true
    },

    linkAgencyLCPhone(state) {
      state.agencyLCPhoneLinked = true
      state.subAccounts = []
    },

    unlinkAgencyTwilio(state) {
      state.agencyTwilioLinked = false
    },

    unlinkAgencyLCPhone(state) {
      state.agencyLCPhoneLinked = false
      state.subAccounts = []
    },

    notify(state, data: Notification) {
      const temp = { ...state.notification, ...data }
      state.notification = temp
      state.notification.visible = true
    },

    setTotalSubAccounts(state, count: number) {
      state.totalSubAccounts = count
    },

    setSubAccounts(state, list: SubAccount[]) {
      state.subAccounts = list
    },

    setSubAccount(state, subAccount: SubAccount) {
      state.subAccount = subAccount
    },

    setPhoneNumber(state, data) {
      const { phoneNumberSID, phoneNumber } = data
      state.phoneNumbers[phoneNumberSID] = phoneNumber
    },

    setSearchQuery(state, query: string) {
      state.searchQuery = query
    },

    setAgencyTwilio(state, payload) {
      state.agencyTwilio = payload
    },

    setTwilioModalForm(state, payload) {
      state.twilioModalForm = {
        accountSid: payload.accountSid,
        autoSubAccounts: payload.autoSubAccounts,
        authToken: payload.authToken,
      }
    },

    setTwilioModal(state, payload) {
      const {
        context,
        cancelCallback,
        successCallback,
        failureCallback,
      } = payload

      state.twilioModal.context = context
      state.twilioModal.cancelCallback = cancelCallback
      state.twilioModal.successCallback = successCallback
      state.twilioModal.failureCallback = failureCallback
      state.twilioModal.showModal = true
    },

    setTablePage(state, page: number) {
      state.tablePage = page
    },

    setStripe(state, payload: any) {
      state.stripe = payload
    },

    startLoading(state) {
      state.loading = true
    },

    stopLoading(state) {
      state.loading = false
    },

    traceFetchReqSubAccounts(state, payload: string) {
      state.traceFetchReq.subAccounts = payload
    },

    resetTablePage(state) {
      // might be obsolete
      state.tablePage = 1
    },

    clearSubAccount(state) {
      // might be obsolete
      state.subAccount = {}
    },

    clearSubAccounts(state) {
      state.subAccounts = []
    },

    clearPhoneNumbers(state) {
      // might be obsolete
      state.phoneNumbers = {}
    },

    clearNotification(state) {
      state.notification = {
        visible: false,
        title: '',
        description: '',
        content: '',
        meta: '',
        duration: 0,
        action: '',
      }
    },

    clearAgencyTwilio(state) {
      state.agencyTwilio = {
        id: '',
        accountSid: '',
        companyId: '',
        traceId: '',
      }
    },

    clearTwilioModalForm(state) {
      state.twilioModalForm = {
        accountSid: '',
        authToken: '',
        autoSubAccounts: false,
      }
    },

    openModalMigration(state, type: MigrationType) {
      state.migration.modal = true
      state.migration.type = type
    },

    closeModalMigration(state) {
      state.migration.modal = false
      state.migration.type = ''
    },

    resetTwilioModal(state) {
      // might be obsolete
      state.twilioModal = {
        context: '' as TwilioLoginModalType,
        showModal: false,
        cancelCallback: undefined,
        successCallback: undefined,
        failureCallback: undefined,
      }
    },
  },

  actions: {
    setAgencyTwilio({ commit }, data) {
      commit('setAgencyTwilio', data)
      data.accountSid
        ? commit('linkAgencyTwilio')
        : commit('unlinkAgencyTwilio')
    },

    openTwilioModal(
      { commit, state },
      data: TwilioModalConfiguration & {
        twilioModalForm: TwilioForm
      }
    ) {
      const {
        context,
        cancelCallback,
        successCallback,
        failureCallback,
        twilioModalForm,
      } = data
      commit('setTwilioModalForm', twilioModalForm)
      commit('setTwilioModal', {
        context,
        cancelCallback,
        successCallback,
        failureCallback,
      })
    },

    closeTwilioModal({ commit }) {
      commit('clearTwilioModalForm')
      commit('resetTwilioModal')
    },

    updateListSubAccountTwilio({ commit, state }, twilioAccountData) {
      const list = cloneDeep(state.subAccounts)
      const index = list.findIndex(
        item => item.id === twilioAccountData.locationId
      )
      // eslint-disable-next-line dot-notation
      list[index]['twilioAccount'] = twilioAccountData

      commit('clearSubAccounts')
      commit('setSubAccounts', list)
    },

    updateListSubAccount({ commit, state }, data) {
      const { updatedSubAccount, updateStoreSubAccount = false } = data
      const list = cloneDeep(state.subAccounts)
      const index = list.findIndex(item => item.id === updatedSubAccount.id)

      if (index >= 0) {
        list[index] = updatedSubAccount
        commit('clearSubAccounts')
        commit('setSubAccounts', list)
      }

      if (updateStoreSubAccount) {
        commit('setSubAccount', list[index])
      }
    },

    async fetchSubAccounts({ commit, state, rootState }, pageIndex) {
      commit('startLoading')
      commit('setTablePage', pageIndex ?? state.tablePage ?? 1)
      commit('clearSubAccounts')

      const { company } = rootState?.company as any
      const { id } = company

      try {
        const payload = {
          companyId: id,
          query: state.searchQuery,
          deleted: false,
          limit: table.SIZE,
          skip: state.tablePage > 1 ? table.OFFSET * (state.tablePage - 1) : 0,
        }

        const res = await TwilioAccountService.list(payload)
        if (res && !isEmpty(res.hit)) {
          if (res && res.locations) {
            const subAccounts = res.locations.map(location => ({
              ...location,
              company: company,
            }))
            commit('setSubAccounts', subAccounts)
            commit('setTotalSubAccounts', res.hit[0].count)
            commit('traceFetchReqSubAccounts', res.traceId)
          }
        }

        commit('stopLoading')
        return Promise.resolve(res)
      } catch (error) {
        commit('stopLoading')
        return Promise.resolve(error)
      }
    },

    async exportDetails({ commit, state, rootState }, companyId) {
      try{
        const res = await CompanyService.exportDetails(companyId)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.resolve(error)
      }
    },

    async fetchTotalSubAccounts({ commit, rootState, state }, query) {
      const { company } = rootState?.company as any
      const { id } = company

      try {
        let subAccounts
        const payload = {
          companyId: id,
          query: query,
          deleted: false,
        }
        const res = await TwilioAccountService.list(payload)
        if (res && !isEmpty(res.hit)) {
          if (res && res.locations) {
            subAccounts = res.locations.map(location => ({
              ...location,
              company: company,
            }))
          }
        }
        return Promise.resolve(subAccounts)
      } catch (error) {
        return Promise.resolve(error)
      }
    },

    async fetchAgency({ commit, rootState }) {
      const { company } = rootState?.company as any
      const { id } = company

      try {
        const { data } = await CompanyService.getById(id)
        // commit('setCompany', data.company)

        data.company.inIsvMode
          ? commit('linkAgencyLCPhone')
          : commit('unlinkAgencyLCPhone')

        return Promise.resolve(data.company)
      } catch (error: any) {
        commit('unlinkAgencyLCPhone')
        return Promise.reject(error)
      }
    },

    async fetchAgencyTwilio({ commit, dispatch, rootState }) {
      const { company } = rootState?.company as any
      const { id } = company
      try {
        const res = await TwilioAccountService.getAgencyTwilio(id)
        dispatch('setAgencyTwilio', res)

        return Promise.resolve(res)
      } catch (error: any) {
        commit('clearAgencyTwilio')
        return Promise.reject(error)
      }
    },

    async validateSubAccountSMS({ commit }, { payload, locationId }) {
      commit('startLoading')

      try {
        const res = await TwilioAccountService.validateSubAccountSMS(
          payload,
          locationId
        )

        commit('stopLoading')
        return Promise.resolve(res)
      } catch (error: any) {
        commit('stopLoading')
        return Promise.reject(error)
      }
    },

    async createSubAccount({ commit }, locationId) {
      try {
        const res = await TwilioAccountService.createSubAccount(locationId)

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async fetchUser({ commit }, userId) {
      try {
        const res = await UserService.getUserById(userId)

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async enableBulkISV({ commit }, { user, companyId }) {
      try {
        const res = TwilioAccountService.bulkEnableISVModeForLocation({
          user,
          companyId,
        })

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async enableSingleISV({ commit }, { user, locationId }) {
      try {
        const res = TwilioAccountService.enableISVModeForLocation({
          user,
          locationId,
        })

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async enableAgencyISV({ commit }, { companyId, stripe, purchaseDescription }) {
      try {
        const res = TwilioAccountService.enableISVModeForCompany(
          companyId,
          stripe,
          purchaseDescription
        )

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async removeTwilioAccount(
      { commit },
      { companyId, locationId, closeSubAccounts }
    ) {
      try {
        const res = await TwilioAccountService.delete(
          companyId,
          locationId,
          closeSubAccounts
        )

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async removeIsvSubAccount(
      { commit },
      {locationId, companyId, deletePhoneNumbers, deleteCampaigns, closeSubAccounts}
    ) {
      try {
        const res = await TwilioAccountService.deleteIsvSubAccount(
          locationId,
          companyId,
          deletePhoneNumbers,
          deleteCampaigns,
          closeSubAccounts
        )

        return res
      } catch (error) {
        throw error
      }
    },
    async updateAgencyTwilio({ commit }, { payload, companyId }) {
      try {
        const check = await TwilioAccountService.checkAccountToken({
          accountSid: payload.accountSID,
          accountToken: payload.accountToken,
        })
        if (check?.data === 'invalid')
          return Promise.reject({ message: 'Invalid Auth token' })

        const res = await TwilioAccountService.updateAgencyTwilio(
          payload,
          companyId
        )

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async updateSubAccountTwilio({ commit }, { payload, locationId }) {
      try {
        const res = await TwilioAccountService.updateSubAccountTwilio(
          payload,
          locationId
        )
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error?.response?.data ?? error)
      }
    },
    async fetchInboundNumbers({ commit }, payload) {
      try {
        const res = await MoveNumbersService.getInboundNumbers(payload)

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async moveNumbers({ commit }, payload) {
      try {
        const res = await MoveNumbersService.moveNumbers(payload)

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
  },
}
